/**
 * pwaUpdatePolicy.js
 *
 * The club-aware service-worker update (PWA install / notifications audit
 * 2026-11-03, Section 3.5, decision D3). Members get the «تحديث متاح» prompt;
 * the collector's day applies a waiting worker by itself once the day is idle,
 * because «لاحقاً» there leaves the page under an old worker whose missing
 * photo-host route fetches the morning's pictures and stores nothing
 * (collector/prewarm.js).
 *
 * usePwa.js wires this to the live worker (`applyUpdateWhenIdle`,
 * `updateWaiting`); it lives apart so it can be tested — usePwa.js imports
 * `virtual:pwa-register/vue`, which exists only inside a vite-plugin-pwa build.
 * No Vue, no store: the worker's state and the reload holds are passed in;
 * the page's own state (an open modal, visibility, focus, navigator.onLine,
 * input) is read here, and the loop guard keeps its stamps in sessionStorage.
 */
import { engagementFor } from '@/clubs/manifest.js';

/** The day must stay idle this long after the last blocking condition clears. */
export const UPDATE_IDLE_MS = 2_000;
/** No idle window for this long → the prompt, once. */
export const UPDATE_FALLBACK_MS = 10 * 60_000;
/** At most this many auto-applies per UPDATE_LOOP_WINDOW_MS in one tab. */
export const UPDATE_LOOP_MAX = 3;
export const UPDATE_LOOP_WINDOW_MS = 10 * 60_000;
/** sessionStorage: the auto-applies of this tab, kept across the reloads they cause. */
export const UPDATE_LOOP_KEY = 'pwa:auto-applied';
// Polled, not watched: an open modal, the page's visibility and navigator.onLine are not reactive.
const UPDATE_TICK_MS = 500;

/**
 * The service-worker update policy for this user (clubs/manifest.js
 * `updatePolicy`): 'prompt' (the dialog) or 'auto-at-idle'. A collector with no
 * club membership reads the collectors' block; a leader who is also a member
 * gets it while on his day, and so does a collector held there after an
 * expired login (audit 2026-11-03, Section 5).
 *
 * @param {{ supplierStage?: string, isSupplier?: boolean, isCollector?: boolean, hasActiveMembership?: boolean }} auth
 * @param {{ onCollectorDay?: boolean }} [where]
 * @returns {'prompt'|'auto-at-idle'}
 */
export function updatePolicyFor(auth, { onCollectorDay = false } = {}) {
  if (onCollectorDay) return 'auto-at-idle';
  return engagementFor({
    supplierStage: auth?.supplierStage,
    isSupplier: auth?.isSupplier,
    isCollector: auth?.isCollector,
    hasActiveMembership: auth?.hasActiveMembership,
  }).updatePolicy;
}

/**
 * An OLD worker controls this page while a new one waits: whatever the page
 * fetches now goes through the old one. No controller means a first install,
 * which has no waiting step.
 *
 * @param {ServiceWorkerContainer|null|undefined} container navigator.serviceWorker
 * @param {ServiceWorkerRegistration|null|undefined} registration
 */
export function oldWorkerWaiting(container, registration) {
  return Boolean(container?.controller && registration?.waiting);
}

function recentAutoApplies(now) {
  try {
    const stamps = JSON.parse(sessionStorage.getItem(UPDATE_LOOP_KEY) ?? '[]');
    return Array.isArray(stamps) ? stamps.filter((at) => now - at < UPDATE_LOOP_WINDOW_MS) : [];
  } catch {
    return [];
  }
}

function recordAutoApply(now) {
  try {
    sessionStorage.setItem(UPDATE_LOOP_KEY, JSON.stringify([...recentAutoApplies(now), now]));
  } catch {
    /* storage blocked: no loop guard; usePwa's one-shot still holds per page */
  }
}

/** No tap, key or scroll for this long — he is not mid-gesture. */
export const UPDATE_QUIET_INPUT_MS = 3_000;
/** An apply that brought no reload is tried again after this long (the loop guard bounds it). */
export const UPDATE_RETRY_MS = 10_000;
const INPUT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

// Inputs that take no typing: a tap leaves focus on them in Android Chrome,
// and they must not hold the update for good.
const NOT_TYPED = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

function typing() {
  const el = typeof document === 'undefined' ? null : document.activeElement;
  if (!el) return false;
  if (el.tagName === 'INPUT') return !NOT_TYPED.has(String(el.type).toLowerCase());
  return el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true;
}

/**
 * The first condition that keeps the update from applying now, or null: no
 * worker actually waiting (a newer deploy made it redundant while the next one
 * installs — SKIP_WAITING would go nowhere), a reload hold (the collector's
 * three queues hold one while they flush, a photo kept only in memory, an AI
 * stream, an open nudge), an open dialog / sheet / popup (a toast is not one),
 * a hidden page (the camera or the file picker is open over it), no signal, a
 * tap / key / scroll in the last UPDATE_QUIET_INPUT_MS or a focused field (a
 * reload between two «اتجاب» would lose his place in the list). Then the
 * caller's `blockers`, each returning a reason string or a falsy value.
 */
export function idleUpdateBlocker({
  workerWaiting,
  isReloadBusy,
  blockers = [],
  lastInputAt = 0,
  now = Date.now(),
}) {
  if (!workerWaiting()) return 'no-waiting-worker';
  // A pocket and no signal first: they are not "the day is busy" (NOT_BUSY),
  // even with a sheet left open or a hold up.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 'hidden';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  if (isReloadBusy()) return 'reload-busy';
  if (typeof document !== 'undefined' && document.querySelector('.modal-in:not(.toast)'))
    return 'modal-open';
  if (now - lastInputAt < UPDATE_QUIET_INPUT_MS) return 'input';
  if (typing()) return 'typing';
  for (const blocker of blockers) {
    const reason = blocker();
    if (reason) return reason;
  }
  return null;
}

// Waiting that says nothing about the day being busy: the phone in a pocket,
// no signal, or no worker waiting any more (another tab applied it, or a newer
// deploy is still installing — a prompt then would offer nothing). It never
// counts toward the fallback prompt.
const NOT_BUSY = new Set(['hidden', 'offline', 'no-waiting-worker']);

let running = null;

/**
 * Apply the waiting worker by itself once the page has been idle for
 * UPDATE_IDLE_MS: no dialog, no «لاحقاً».
 *
 * After UPDATE_FALLBACK_MS of the day being busy (visible, with signal) and no
 * idle window, `onFallback` is called once (the prompt), and the run carries
 * on: the open dialog is a modal, so the run waits while it is up, and after
 * «لاحقاً» it still applies at the next idle moment; deferring is the trap
 * this exists to remove. An apply that brought no reload (SKIP_WAITING went
 * nowhere) is tried again UPDATE_RETRY_MS later. It stops by itself when
 * `needRefresh` goes false, and after UPDATE_LOOP_MAX auto-applies in
 * UPDATE_LOOP_WINDOW_MS (a worker that never takes control would otherwise
 * reload the day again and again) — then with the prompt, if it never came.
 * One run at a time: a second call while one runs returns the running one's
 * stop.
 *
 * @param {object} options
 * @param {() => boolean} options.needRefresh   a worker is waiting (vite-plugin-pwa).
 * @param {() => boolean} options.workerWaiting the registration holds a waiting worker now.
 * @param {() => boolean} options.isReloadBusy  usePwa's reload holds.
 * @param {() => boolean|void} options.apply  SKIP_WAITING + reload; `false` when
 *   nothing was posted (an update already in flight) — not counted by the loop guard.
 * @param {Array<() => string|false|null|undefined>} [options.blockers]
 * @param {() => void}    [options.onFallback]
 * @returns {() => void} stop
 */
export function startIdleUpdate({
  needRefresh,
  workerWaiting,
  isReloadBusy,
  apply,
  blockers = [],
  onFallback = null,
}) {
  if (running) return running.stop;
  let lastTick = Date.now();
  let busyMs = 0;
  let idleSince = null;
  let appliedAt = null;
  let fellBack = false;
  let lastInputAt = 0;
  let timer = null;
  const onInput = () => {
    lastInputAt = Date.now();
  };
  const listening = typeof document !== 'undefined';
  if (listening) {
    INPUT_EVENTS.forEach((name) =>
      document.addEventListener(name, onInput, { capture: true, passive: true }),
    );
  }
  const fallBack = () => {
    if (fellBack) return;
    fellBack = true;
    onFallback?.();
  };
  const run = {
    stop() {
      clearInterval(timer);
      if (listening) {
        INPUT_EVENTS.forEach((name) =>
          document.removeEventListener(name, onInput, { capture: true }),
        );
      }
      if (running === run) running = null;
    },
  };
  running = run;

  const tick = () => {
    if (!needRefresh()) {
      run.stop();
      return;
    }
    const now = Date.now();
    // A frozen or throttled timer (screen off, page in the background) comes
    // back with minutes in one tick: count no more than a tick or two of it.
    const elapsed = Math.min(now - lastTick, UPDATE_TICK_MS * 2);
    lastTick = now;
    if (appliedAt !== null && now - appliedAt < UPDATE_RETRY_MS) return;
    if (recentAutoApplies(now).length >= UPDATE_LOOP_MAX) {
      console.warn('[PWA] auto update stopped: too many in a row');
      fallBack();
      run.stop();
      return;
    }
    const blocker = idleUpdateBlocker({ workerWaiting, isReloadBusy, blockers, lastInputAt, now });
    if (!blocker) {
      idleSince ??= now;
      if (now - idleSince >= UPDATE_IDLE_MS) {
        idleSince = null;
        appliedAt = now;
        if (apply() !== false) recordAutoApply(now);
      }
      return;
    }
    idleSince = null;
    if (!NOT_BUSY.has(blocker)) busyMs += elapsed;
    if (busyMs >= UPDATE_FALLBACK_MS) fallBack();
  };
  timer = setInterval(tick, UPDATE_TICK_MS);
  tick();
  return run.stop;
}

/** Stop the running auto-apply, if any (usePwa's unregister, tests). */
export function stopIdleUpdate() {
  running?.stop();
}
